import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Ban, Search, AlertTriangle, Trash2 } from "lucide-react";

const EMPTY = {
  razon_social: "", nombre_comercial: "", contacto_nombre: "", email: "",
  telefono: "", terminos_pago_dias: 30, direccion: "", pais: "US", notas: "", contactos: [],
};

export default function Clients() {
  const { can } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [activo, setActivo] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [similar, setSimilar] = useState([]);
  const [saving, setSaving] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateMsg, setDeactivateMsg] = useState("");

  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", {
        params: { search, activo, skip: page * limit, limit },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, activo, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setSimilar([]);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, contactos: c.contactos || [] });
    setSimilar([]);
    setOpen(true);
  };

  const addContacto = () => setForm({ ...form, contactos: [...(form.contactos || []), { nombre: "", cargo: "", email: "", telefono: "" }] });
  const removeContacto = (idx) => setForm({ ...form, contactos: form.contactos.filter((_, i) => i !== idx) });
  const updateContacto = (idx, field, value) => {
    const next = [...form.contactos];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, contactos: next });
  };

  const save = async (force = false) => {
    setSaving(true);
    try {
      const payload = { ...form, contactos: (form.contactos || []).filter((c) => (c.nombre || "").trim()) };
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await api.post(`/clients?force=${force}`, payload);
        toast.success("Cliente creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "posible_duplicado") {
        setSimilar(detail.similar || []);
        toast.warning("Se encontraron clientes con nombre parecido");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const doDeactivate = async (force = false) => {
    try {
      await api.post(`/clients/${deactivateTarget.id}/deactivate?force=${force}`);
      toast.success("Cliente desactivado");
      setDeactivateTarget(null);
      setDeactivateMsg("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "tiene_loads") {
        setDeactivateMsg(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
        setDeactivateTarget(null);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle="Brokers y clientes a los que se les factura"
        actions={
          can("clients", "crear") && (
            <Button onClick={openCreate} data-testid="new-client-btn" className="gap-2">
              <Plus className="h-4 w-4" /> Nuevo cliente
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por código, razón social o nombre comercial"
            value={search}
            onChange={(e) => { setPage(0); setSearch(e.target.value); }}
            className="pl-9"
            data-testid="client-search"
          />
        </div>
        <Select value={activo} onValueChange={(v) => { setPage(0); setActivo(v); }}>
          <SelectTrigger className="w-full sm:w-44" data-testid="client-filter-activo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="true">Activos</SelectItem>
            <SelectItem value="false">Inactivos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Razón social</TableHead>
              <TableHead>Nombre comercial</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead className="text-right">Términos</TableHead>
              <TableHead>País</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.codigo}`} className={!c.activo ? "opacity-60" : ""}>
                <TableCell className="font-mono text-xs">{c.codigo}</TableCell>
                <TableCell className="font-medium">{c.razon_social}</TableCell>
                <TableCell className="text-muted-foreground">{c.nombre_comercial}</TableCell>
                <TableCell className="text-muted-foreground">{c.contacto_nombre || "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{c.terminos_pago_dias}d</TableCell>
                <TableCell className="font-mono text-xs">{c.pais}</TableCell>
                <TableCell>
                  {c.activo ? (
                    <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50">Activo</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactivo</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {can("clients", "editar") && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.codigo}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {can("clients", "desactivar") && c.activo && (
                      <Button variant="ghost" size="icon" onClick={() => setDeactivateTarget(c)} data-testid={`deactivate-client-${c.codigo}`}>
                        <Ban className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                  Sin resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span>{data.total} cliente(s)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Pág. {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>

      {/* Create / Edit Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-heading">{editing ? "Editar cliente" : "Nuevo cliente"}</SheetTitle>
          </SheetHeader>

          {similar.length > 0 && (
            <div className="my-4 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="client-duplicate-warning">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posibles duplicados
              </div>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {similar.map((s) => (
                  <li key={s.id} className="font-mono text-xs">• {s.codigo} — {s.razon_social}</li>
                ))}
              </ul>
              <Button size="sm" className="mt-3" variant="outline" onClick={() => save(true)} data-testid="client-force-create">
                Crear de todos modos
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Razón social *" className="sm:col-span-2">
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="client-razon-social" />
            </Field>
            <Field label="Nombre comercial" className="sm:col-span-2">
              <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} data-testid="client-nombre-comercial" />
            </Field>
            <Field label="Contacto">
              <Input value={form.contacto_nombre} onChange={(e) => setForm({ ...form, contacto_nombre: e.target.value })} />
            </Field>
            <Field label="Teléfono">
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Términos de pago (días)">
              <Input type="number" value={form.terminos_pago_dias} onChange={(e) => setForm({ ...form, terminos_pago_dias: parseInt(e.target.value || 0) })} data-testid="client-terminos" />
            </Field>
            <Field label="País">
              <Select value={form.pais} onValueChange={(v) => setForm({ ...form, pais: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">US</SelectItem>
                  <SelectItem value="MX">MX</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Dirección">
              <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
            </Field>
            <Field label="Notas" className="sm:col-span-2">
              <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </Field>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Contactos</Label>
              <Button type="button" size="sm" variant="outline" onClick={addContacto} className="gap-1" data-testid="client-add-contacto">
                <Plus className="h-3.5 w-3.5" /> Agregar contacto
              </Button>
            </div>
            {(!form.contactos || form.contactos.length === 0) && (
              <p className="text-xs text-muted-foreground">Sin contactos agregados.</p>
            )}
            <div className="space-y-2">
              {(form.contactos || []).map((ct, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-md border border-border p-2" data-testid={`client-contacto-${i}`}>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Nombre</Label>
                    <Input className="mt-1 h-9" value={ct.nombre} onChange={(e) => updateContacto(i, "nombre", e.target.value)} data-testid={`client-contacto-${i}-nombre`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Cargo</Label>
                    <Input className="mt-1 h-9" value={ct.cargo || ""} onChange={(e) => updateContacto(i, "cargo", e.target.value)} data-testid={`client-contacto-${i}-cargo`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Email</Label>
                    <Input className="mt-1 h-9" value={ct.email || ""} onChange={(e) => updateContacto(i, "email", e.target.value)} data-testid={`client-contacto-${i}-email`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-muted-foreground">Teléfono</Label>
                    <Input className="mt-1 h-9" value={ct.telefono || ""} onChange={(e) => updateContacto(i, "telefono", e.target.value)} data-testid={`client-contacto-${i}-telefono`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeContacto(i)} data-testid={`client-contacto-${i}-remove`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => save(false)} disabled={saving || !form.razon_social} data-testid="client-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Deactivate confirm */}
      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) { setDeactivateTarget(null); setDeactivateMsg(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar cliente</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateMsg || `¿Desactivar a ${deactivateTarget?.razon_social}? No se elimina, solo se marca como inactivo.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDeactivate(!!deactivateMsg)} data-testid="confirm-deactivate-client">
              {deactivateMsg ? "Desactivar de todos modos" : "Desactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
